import React, { useState, useMemo, useCallback, useRef } from 'react'; // Removed useEffect
import Slider from 'react-slick'; // Import Slider
import './Skills.css';
import { FaRandom } from 'react-icons/fa';

// Import slick carousel CSS if not imported globally in index.js or App.js
// import "slick-carousel/slick/slick.css";
// import "slick-carousel/slick/slick-theme.css";

// Define skills data based on cv.txt
// Ratings are normalized to a 0-10 scale for simplicity in styling
const skillsData = [
  // Main Skills
  { name: 'App Dev', category: 'main', rating: 9.5, cardId: 'app-dev' },
  { name: 'Machine Learning', category: 'main', rating: 9.5, cardId: 'ml-dl' },
  { name: 'Deep Learning', category: 'main', rating: 9.5, cardId: 'ml-dl' },
  { name: 'Computer Vision', category: 'main', rating: 9.0, cardId: 'cv' },
  { name: 'Web Dev', category: 'main', rating: 5.0, cardId: 'web-dev' },
  { name: 'Mobile Dev', category: 'main', rating: 6.0, cardId: 'mobile-dev' },
  // Programming Languages
  { name: 'C/C++', category: 'language', rating: 9.5, cardId: 'app-dev' },
  { name: 'Python', category: 'language', rating: 9.5, cardId: 'ml-dl' }, // Also used in App Dev
  { name: 'C#', category: 'language', rating: 7.0, cardId: 'app-dev' }, // Primarily App Dev/Unity
  { name: 'Swift', category: 'language', rating: 5.0, cardId: 'mobile-dev' },
  { name: 'Dart', category: 'language', rating: 5.0, cardId: 'mobile-dev' },
  { name: 'Racket', category: 'language', rating: 5.0, cardId: 'other-langs' },
  { name: 'Prolog', category: 'language', rating: 5.0, cardId: 'other-langs' },
  { name: 'Javascript', category: 'language', rating: 5.0, cardId: 'web-dev' }, // Added cardId
  // Frameworks
  { name: 'React', category: 'framework', rating: 5.0, cardId: 'web-dev' }, // Added React
  { name: 'PyTorch', category: 'framework', rating: 9.5, cardId: 'ml-dl' },
  { name: 'Keras', category: 'framework', rating: 9.0, cardId: 'ml-dl' },
  { name: 'OpenCV', category: 'framework', rating: 9.0, cardId: 'cv' },
  { name: 'Qt', category: 'framework', rating: 9.0, cardId: 'app-dev' },
  { name: 'Tensorflow', category: 'framework', rating: 8.0, cardId: 'ml-dl' },
  { name: 'FFMPEG', category: 'framework', rating: 7.0, cardId: 'app-dev' }, // Multimedia/App Dev
  { name: 'CUDA', category: 'framework', rating: 6.0, cardId: 'ml-dl' }, // GPU for ML/DL
  { name: 'GStreamer', category: 'framework', rating: 7.0, cardId: 'app-dev' }, // Multimedia/App Dev
  { name: 'OpenGL', category: 'framework', rating: 6.5, cardId: 'graphics-design' }, // Graphics
  { name: 'V4L2', category: 'framework', rating: 6.0, cardId: 'app-dev' }, // Linux Video/App Dev
  { name: 'Flutter', category: 'framework', rating: 5.5, cardId: 'mobile-dev' },
  // Other Tools
  { name: 'Windows', category: 'tool', rating: 8.0, cardId: 'dev-environment' },
  { name: 'Linux', category: 'tool', rating: 8.0, cardId: 'dev-environment' },
  { name: 'MacOS', category: 'tool', rating: 7.0, cardId: 'dev-environment' },
  { name: 'GIT', category: 'tool', rating: 9.0, cardId: 'dev-environment' },
  { name: 'MS Office', category: 'tool', rating: 8.0, cardId: 'dev-environment' },
  { name: 'XCode', category: 'tool', rating: 5.0, cardId: 'mobile-dev' },
  { name: 'Unity', category: 'tool', rating: 6.5, cardId: 'graphics-design' }, // Game Dev/Graphics
  { name: 'Blender', category: 'tool', rating: 5.0, cardId: 'graphics-design' },
  { name: 'Fusion360', category: 'tool', rating: 5.0, cardId: 'graphics-design' },
];

// Define card content structure
const skillCardsContent = {
  'app-dev': {
    title: 'Application Development', // Removed (Core Focus)
    content: 'A core focus of my work involves leveraging years of professional experience in building robust desktop and server applications using C/C++, C#, Qt, and multimedia libraries (FFMPEG, GStreamer, V4L2). Focused on developing high-performance, resource-optimized software.', // Added "A core focus..."
    skills: ['Application Development', 'C/C++', 'C#', 'Qt', 'FFMPEG', 'GStreamer', 'V4L2']
  },
  'ml-dl': {
    title: 'Machine Learning & Deep Learning', // Removed (Core Focus)
    content: 'As a core focus, I develop sophisticated ML/DL models with Python, PyTorch, Keras, Tensorflow, and CUDA, backed by a strong academic and practical background. Experienced in neural networks, computer vision algorithms, and data analysis pipelines.', // Added "As a core focus..."
    skills: ['Machine Learning', 'Deep Learning', 'Python', 'PyTorch', 'Keras', 'Tensorflow', 'CUDA']
  },
  'cv': {
    title: 'Computer Vision',
    content: 'Working with advanced computer vision tasks using OpenCV and custom algorithms, applied in professional projects. Expertise in image processing, object recognition, and video analysis techniques.',
    skills: ['Computer Vision', 'OpenCV']
  },
  'mobile-dev': {
    title: 'Mobile App Development',
    content: 'Exploring cross-platform and native mobile development with Swift, Dart, Flutter, and XCode. Building responsive, user-friendly applications for iOS and Android platforms.',
    skills: ['Swift', 'Dart', 'Flutter', 'XCode']
  },
  'web-dev': {
    title: 'Web Development',
    content: 'Building interactive web applications using Javascript, React, and modern web technologies. Familiar with responsive design principles and front-end development practices.',
    skills: ['Javascript', 'React']
  },
  'graphics-design': {
    title: 'Graphics & Game Development',
    content: 'Experience with OpenGL, Unity, Blender, and Fusion360 for graphics, 3D modeling, and basic game development, including professional use of OpenGL. Creating interactive experiences and 3D visualizations.',
    skills: ['OpenGL', 'Unity', 'Blender', 'Fusion360']
  },
  'dev-environment': {
    title: 'Development Environment',
    content: 'Proficient across Windows, Linux, and MacOS environments with strong command of essential development tools like GIT and MS Office. Experienced in setting up and managing cross-platform development workflows.',
    skills: ['Windows', 'Linux', 'MacOS', 'GIT', 'MS Office']
  },
  'other-langs': {
      title: 'Other Languages',
      content: 'Familiarity with functional and logic programming paradigms (Racket, Prolog) for exploring alternative approaches to computational problems.',
      skills: ['Racket', 'Prolog']
  }
  // Add more cards as needed
};

// Function to calculate a rating factor (0 to 1) based on rating
const calculateRatingFactor = (rating) => {
  const minRating = 5;    // Actual minimum rating in skillsData
  const maxRating = 10;   // Maximum rating expected
  // Normalize rating to a 0-1 factor
  const factor = (rating - minRating) / (maxRating - minRating);
  return Math.max(0, Math.min(1, factor)); // Clamp between 0 and 1, ensuring it's within [0, 1]
};

const Skills = ({ id }) => {
  const [selectedSkillName, setSelectedSkillName] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [shuffleKey, setShuffleKey] = useState(0);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const sliderRef = useRef(null); // Ref for react-slick slider

  const shuffledSkills = useMemo(
    () => [...skillsData].sort(() => Math.random() - 0.5),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shuffleKey] // shuffleKey is intentionally used to trigger re-memoization
  );

  const uniqueCards = useMemo(
    () => Object.entries(skillCardsContent).map(([id, data]) => ({ id, ...data })),
    []
  );

  const handleSkillClick = (skill) => {
    if (selectedSkillName === skill.name) {
      // Deselecting the skill
      setSelectedSkillName(null);
      setSelectedCardId(null);
    } else {
      // Selecting a new skill
      setSelectedSkillName(skill.name);
      setSelectedCardId(skill.cardId);

      // Find the index of the card to navigate to
      const cardIndex = uniqueCards.findIndex(card => card.id === skill.cardId);
      if (cardIndex !== -1 && sliderRef.current) {
        sliderRef.current.slickGoTo(cardIndex); // Navigate slider
      }
    }
  };

  // New handler for clicking a card directly
  const handleCardClick = (cardId, cardIndex) => {
    if (selectedCardId === cardId) {
      // Deselecting the card
      setSelectedCardId(null);
      setSelectedSkillName(null); // Also deselect skill tag if card is deselected
    } else {
      // Selecting the card
      setSelectedCardId(cardId);
      setSelectedSkillName(null); // Deselect any selected skill tag
      if (sliderRef.current) {
        sliderRef.current.slickGoTo(cardIndex); // Center the clicked card
      }
    }
  };


  const triggerShuffle = useCallback(() => setShuffleKey((prev) => prev + 1), []);

  const handleCardHover = useCallback((cardId) => setHoveredCardId(cardId), []);
  const handleCardLeave = useCallback(() => setHoveredCardId(null), []);

  const isSkillRelevantToHoveredCard = useCallback(
    (skill) => {
      if (!hoveredCardId) return true; // Show all if no card is hovered
      // If a skill is selected, only check against the selected card's hover state
      if (selectedCardId && hoveredCardId !== selectedCardId) return true;
      // If no skill is selected OR the hovered card matches the selected one, apply opacity
      const cardContent = skillCardsContent[hoveredCardId];
      return cardContent?.skills.includes(skill.name);
    },
    [hoveredCardId, selectedCardId] // Add selectedCardId dependency
  );

  // Settings for react-slick
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3, // Show 3 cards at a time
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000, // Slide every 3 seconds
    pauseOnHover: true,
    centerMode: true, // Center the active slide
    centerPadding: '0px', // Adjust padding if needed
    responsive: [ // Adjust slidesToShow for different screen sizes
      {
        breakpoint: 1024, // Medium screens
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 600, // Small screens
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };

  return (
    <section id={id} className="skills-section">
      <h2>Skills</h2>
      <div className="tag-cloud-wrapper">
        <button onClick={triggerShuffle} className="shuffle-button" aria-label="Shuffle skills">
          <FaRandom />
        </button>
        <div className="tag-cloud-container">
          {shuffledSkills.map((skill) => (
            <span
              key={skill.name}
              className={`skill-tag category-${skill.category} ${
                selectedSkillName === skill.name ? 'selected' : ''
              } ${hoveredCardId && !isSkillRelevantToHoveredCard(skill) ? 'opaque' : ''}`}
              style={{ '--rating-factor': calculateRatingFactor(skill.rating) }}
              onClick={() => handleSkillClick(skill)} // Keep existing skill click handler
            >
              {skill.name}
            </span>
          ))}
        </div>
      </div>
      {/* Replace custom carousel with react-slick Slider */}
      <div className="skill-cards-outer-container"> {/* Keep outer container for styling if needed */}
        <Slider ref={sliderRef} {...sliderSettings}>
          {uniqueCards.map((card, index) => ( // Added index here
            <div
              key={card.id} // Use card.id as key
              className={`skill-card-wrapper ${selectedCardId === card.id ? 'highlighted-wrapper' : ''}`} // Add wrapper for padding/margin needed by slick
              onMouseEnter={() => handleCardHover(card.id)}
              onMouseLeave={handleCardLeave}
              onClick={() => handleCardClick(card.id, index)} // Added onClick handler here
            >
              <div className={`skill-card ${selectedCardId === card.id ? 'highlighted' : ''}`}>
                <h3>{card.title}</h3>
                <p>{card.content}</p>
              </div>
            </div>
          ))}
        </Slider>
      </div>
    </section> // Added missing closing tag
  );
};

export default Skills;
